/**
 * dealEdit.ts — the pure decisions behind the Deal properties editor (Slice E,
 * §15.11 decisions E-1, E-2, E-7), kept out of the component so vitest can pin
 * them without a DOM.
 *
 * Three rules live here:
 *  - SCOPE (E-1, extended by 2.2): title, value, currency, expected close,
 *    owner, priority, source, account re-pick, and — since 2.2 — the deal's
 *    OWN probability. Contact and free-text company are NOT offered:
 *    re-pointing a contact strands a lead's converted_deal_id, and company
 *    text is untracked (A2).
 *  - PROBABILITY (2.2, P2-7): the input edits the deal's OWN assessment. A
 *    blank input means "not assessed — inherit the stage ladder" and travels
 *    as clearProbability; a number 0-100 travels as probability (0 is a real
 *    assessment). The EFFECTIVE value the deal currently carries is shown
 *    beside the input as the placeholder, never written into it.
 *  - FORECAST CATEGORY (2.3a, F-2 / F-3): the picker carries the four slugs
 *    plus Uncategorised. Uncategorised is NOT a fifth value — it is what NULL
 *    reads as, so choosing it travels as clearForecastCategory, exactly the
 *    clearProbability shape. The draft holds '' for it, and the two wire
 *    fields are exclusive by construction here, as the server requires.
 *  - FROZEN ON CLOSED (E-2): value, currency, expected close and priority are
 *    fixed once a deal is closed_won/closed_lost — a closed-won deal's value
 *    backs reported revenue and the invoice issued at close. The server
 *    refuses them with a named 409; this module makes the editor NOT OFFER
 *    them there, with the reason, so nobody types into a field that will be
 *    refused (E2.3). The 409 is the backstop, not the UX.
 *  - CHANGED FIELDS ONLY (E2.2): the PUT body is the DIFF between the stored
 *    record and the draft, never the whole form — so one intended edit yields
 *    one field-history row per field actually changed.
 */
import type { UpdateDealRequest } from '../../../../api/types'
import type { DealDetail, DealStage } from '../../types'

export interface DealEditDraft {
  title: string
  /** Decimal pounds as typed; '' = unchanged/unset. */
  value: string
  currency: string
  /** yyyy-mm-dd from the date input; '' = unchanged/unset. */
  expectedClose: string
  /** The employee ID (E360-2 owner wire); '' = unchanged. */
  assignedEmployeeId: string
  priority: string
  source: string
  /** Account re-pick: the picked account's id + canonical name; '' = unchanged. */
  accountId: string
  accountName: string
  /** The deal's OWN probability as typed; '' = not assessed (inherit). */
  probability: string
  /** 2.3a: the chosen slug, or '' = Uncategorised (which SAVES as a clear,
   *  not as an empty value). */
  forecastCategory: string
}

export type DealEditField = keyof DealEditDraft

export function isClosedStage(stage: DealStage): boolean {
  return stage === 'Closed Won' || stage === 'Closed Lost'
}

/** The fields E-2 freezes once a deal is closed. Mirrors the server guard
 *  (refuseFrozenEditsOnClosedDeal) exactly — probability since 2.2, and the
 *  forecast category since 2.3a: a closed deal's forecast is history, and the
 *  server names "forecast category" in the same 409. */
export const FROZEN_WHEN_CLOSED: ReadonlyArray<DealEditField> = ['value', 'currency', 'expectedClose', 'priority', 'probability', 'forecastCategory']

/** The short reason shown beside a frozen field on a closed deal (E2.3). */
export const FROZEN_REASON = 'Fixed at close — this value backs reported revenue and the issued invoice.'

/** Whether a given field may be edited on a deal in this stage. */
export function fieldEditable(field: DealEditField, stage: DealStage): boolean {
  if (!isClosedStage(stage)) return true
  return !FROZEN_WHEN_CLOSED.includes(field)
}

/** ISO instant → the date input's yyyy-mm-dd (UTC date), '' when absent. */
export function isoToDateInput(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

/** Integer pence → the value input's decimal pounds string. */
export function penceToPoundsInput(pence: number): string {
  return (pence / 100).toFixed(2)
}

/** The draft the editor opens with — the stored record, rendered for inputs. */
export function draftFromDeal(d: DealDetail): DealEditDraft {
  return {
    title: d.title,
    value: penceToPoundsInput(d.amountPence),
    currency: d.currency,
    expectedClose: isoToDateInput(d.expectedClose),
    assignedEmployeeId: d.assignedEmployeeId ?? '',
    priority: d.priority,
    source: d.source,
    accountId: d.accountId ?? '',
    accountName: d.company,
    probability: d.probability == null ? '' : String(d.probability),
    forecastCategory: d.forecastCategory ?? '',
  }
}

/** A typed probability is valid when blank (inherit) or an integer 0-100. */
export function probabilityInputValid(v: string): boolean {
  if (v.trim() === '') return true
  const n = Number(v)
  return Number.isInteger(n) && n >= 0 && n <= 100
}

/** The short copy for how a deal's probability came to be (P2-7): a rep must
 *  be able to tell "nobody assessed this, it inherits 50" from "someone set
 *  it to 50". Returns '' when there is nothing to say (closed, unassessed). */
export function probabilitySourceLabel(source: DealDetail['probabilitySource']): string {
  switch (source) {
    case 'deal': return 'assessed on this deal'
    case 'tenant': return 'inherited from your stage ladder'
    case 'default': return 'inherited from the platform default'
    default: return ''
  }
}

/** A typed value is valid when empty (unchanged) or a finite non-negative
 *  number. Zero is refused HERE because the server's keep-current SET treats
 *  value 0 as "keep" (value_pence updates only when $7 > 0), so a 0 would be a
 *  silent no-op that looked like a save. */
export function valueInputValid(v: string): boolean {
  if (v.trim() === '') return true
  const n = Number(v)
  return Number.isFinite(n) && n > 0
}

export function dealEditSavable(before: DealDetail, draft: DealEditDraft, busy: boolean): boolean {
  if (busy) return false
  if (draft.title.trim() === '') return false
  if (!valueInputValid(draft.value)) return false
  if (!probabilityInputValid(draft.probability)) return false
  return Object.keys(dealEditDiff(before, draft)).length > 0
}

/**
 * The changed-fields-only body (E2.2). Compares each draft field against the
 * STORED record in the wire's own units, and emits a field only when it
 * differs. Frozen fields on a closed deal are never emitted even if the draft
 * somehow carries them — belt and braces under the server guard.
 *
 * Account re-pick emits accountId AND company = the canonical name, the
 * Slice A picker contract ("the input's text IS the deal's company; picking a
 * row also sets accountId"), so the two never drift apart on the wire.
 */
export function dealEditDiff(before: DealDetail, draft: DealEditDraft): UpdateDealRequest {
  const body: UpdateDealRequest = {}
  const closed = isClosedStage(before.stage)
  const may = (f: DealEditField) => !closed || !FROZEN_WHEN_CLOSED.includes(f)

  const title = draft.title.trim()
  if (title !== '' && title !== before.title) body.title = title

  if (may('value') && draft.value.trim() !== '') {
    const pounds = Number(draft.value)
    if (Number.isFinite(pounds) && pounds > 0 && Math.round(pounds * 100) !== before.amountPence) body.value = pounds
  }
  if (may('currency') && draft.currency !== '' && draft.currency !== before.currency) body.currency = draft.currency
  if (may('expectedClose') && draft.expectedClose !== '' && draft.expectedClose !== isoToDateInput(before.expectedClose)) {
    // Go *time.Time is strict RFC3339 — never a bare date.
    body.expectedClose = `${draft.expectedClose}T00:00:00Z`
  }
  if (draft.assignedEmployeeId !== '' && draft.assignedEmployeeId !== (before.assignedEmployeeId ?? '')) {
    body.assignedEmployeeId = draft.assignedEmployeeId
  }
  if (may('priority') && draft.priority !== '' && draft.priority !== before.priority) body.priority = draft.priority
  // SB-6: a stored legacy source pre-selects but never rides the wire unless
  // the user actually changed it (validateSource would refuse it).
  if (draft.source !== '' && draft.source !== before.source) body.source = draft.source
  if (draft.accountId !== '' && draft.accountId !== (before.accountId ?? '')) {
    body.accountId = draft.accountId
    if (draft.accountName.trim() !== '') body.company = draft.accountName.trim()
  }
  // 2.2: blank = inherit (clearProbability, only when the deal HAD its own
  // value); a valid number = the deal's own assessment, only when it changed.
  // The two are exclusive by construction here, as the server requires.
  if (may('probability') && probabilityInputValid(draft.probability)) {
    const typed = draft.probability.trim()
    if (typed === '') {
      if (before.probability != null) body.clearProbability = true
    } else if (Number(typed) !== before.probability) {
      body.probability = Number(typed)
    }
  }
  // 2.3a: '' is Uncategorised, which is a CLEAR — and only when the deal
  // actually HAD a category, so re-saving an already-uncategorised deal emits
  // nothing and writes no history row (E2.2 / B-6). A slug rides only when it
  // changed. The two can never both be set from here.
  if (may('forecastCategory')) {
    const picked = draft.forecastCategory
    if (picked === '') {
      if (before.forecastCategory != null) body.clearForecastCategory = true
    } else if (picked !== before.forecastCategory) {
      body.forecastCategory = picked
    }
  }
  return body
}
